import { createContext, type FC, type ReactNode, useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useRevalidator } from '@remix-run/react';

import type { NectarPageBlock } from '~/utils/nectarPageBlock';

import { BanMemberDialog } from '../_components/ui/BanMemberDialog';
import { BlockMemberDialog } from '../_components/ui/BlockMemberDialog';
import { ConfirmDialog } from '../_components/ui/ConfirmDialog';

import { useBanMemberMutation } from '../_hooks/useBanMemberMutation';
import { useBlockMemberMutation } from '../_hooks/useBlockMemberMutation';
import { useFollowMemberMutation } from '../_hooks/useFollowMemberMutation';
import { useUnbanMemberMutation } from '../_hooks/useUnbanMemberMutation';

interface MemberTarget {
  id: string;
  name: string;
}

interface ToggleFollowOptions {
  pageBlock?: NectarPageBlock;
  onSuccess?: () => void;
  onSettled?: () => void;
}

interface CommunityMemberActions {
  openBanDialog: (member: MemberTarget) => void;
  openUnbanDialog: (member: MemberTarget) => void;
  openBlockDialog: (member: MemberTarget) => void;
  openUnblockDialog: (member: MemberTarget) => void;
  toggleFollow: (member: MemberTarget, isFollowing: boolean, options?: ToggleFollowOptions) => void;
  isFollowPending: (memberId: string) => boolean;
}

const CommunityMemberActionsContext = createContext<CommunityMemberActions | null>(null);

// biome-ignore lint/style/useComponentExportOnlyModules: provider co-locates its context hook by design
export const useCommunityMemberActions = (): CommunityMemberActions => {
  const ctx = useContext(CommunityMemberActionsContext);
  if (!ctx) throw new Error('useCommunityMemberActions must be used within CommunityMemberActionsProvider');
  return ctx;
};

type ActiveModal = { type: 'ban' | 'unban' | 'block' | 'unblock'; member: MemberTarget } | null;

export const CommunityMemberActionsProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const { revalidate } = useRevalidator();
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const [pendingFollow, setPendingFollow] = useState<ReadonlySet<string>>(new Set());
  // Synchronous guard so a double-click on block/unblock fires one request, not two;
  // isPending re-renders too late to catch the second click.
  const blockSubmittingRef = useRef(false);

  const banMutation = useBanMemberMutation();
  const unbanMutation = useUnbanMemberMutation();
  const blockMutation = useBlockMemberMutation();
  const followMutation = useFollowMemberMutation();

  const closeModal = () => setActiveModal(null);

  const value = useMemo<CommunityMemberActions>(
    () => ({
      openBanDialog: (member) => setActiveModal({ type: 'ban', member }),
      openUnbanDialog: (member) => setActiveModal({ type: 'unban', member }),
      openBlockDialog: (member) => setActiveModal({ type: 'block', member }),
      openUnblockDialog: (member) => setActiveModal({ type: 'unblock', member }),
      toggleFollow: (member, isFollowing, options) => {
        setPendingFollow((prev) => new Set(prev).add(member.id));
        followMutation.mutate(
          { memberId: member.id, follow: !isFollowing, pageBlock: options?.pageBlock },
          {
            onSuccess: () => {
              revalidate();
              options?.onSuccess?.();
            },
            onSettled: () => {
              setPendingFollow((prev) => {
                const next = new Set(prev);
                next.delete(member.id);
                return next;
              });
              options?.onSettled?.();
            },
          },
        );
      },
      isFollowPending: (memberId) => pendingFollow.has(memberId),
    }),
    [followMutation.mutate, pendingFollow, revalidate],
  );

  const member = activeModal?.member;

  return (
    <CommunityMemberActionsContext.Provider value={value}>
      {children}

      <ConfirmDialog
        isOpen={activeModal?.type === 'unban'}
        title={t('community.action_menu.unban_member_confirm_title', { memberName: member?.name ?? '' })}
        description={t('community.action_menu.unban_member_confirm_description')}
        confirmLabel={t('community.action_menu.unban_member_confirm_button')}
        onConfirm={() => {
          if (!member) return;
          unbanMutation.mutate(
            { memberId: member.id },
            {
              onSuccess: () => {
                closeModal();
                revalidate();
              },
            },
          );
        }}
        onCancel={closeModal}
        isLoading={unbanMutation.isPending}
      />

      <BanMemberDialog
        key={activeModal?.type === 'ban' ? `ban-${member?.id}` : 'ban-closed'}
        isOpen={activeModal?.type === 'ban'}
        memberName={member?.name ?? ''}
        onConfirm={(reason) => {
          if (!member) return;
          banMutation.mutate(
            { memberId: member.id, ...(reason ? { reason } : {}) },
            {
              onSuccess: () => {
                closeModal();
                revalidate();
              },
            },
          );
        }}
        onCancel={closeModal}
        isLoading={banMutation.isPending}
      />

      <BlockMemberDialog
        isOpen={activeModal?.type === 'block'}
        memberName={member?.name ?? ''}
        onConfirm={() => {
          if (!member || blockSubmittingRef.current) return;
          blockSubmittingRef.current = true;
          blockMutation.mutate(
            { memberId: member.id, block: true },
            {
              onSuccess: () => {
                closeModal();
                revalidate();
              },
              onSettled: () => {
                blockSubmittingRef.current = false;
              },
            },
          );
        }}
        onCancel={closeModal}
        isLoading={blockMutation.isPending}
      />

      <ConfirmDialog
        isOpen={activeModal?.type === 'unblock'}
        title={t('community.action_menu.unblock_member_confirm_title', { memberName: member?.name ?? '' })}
        description={t('community.action_menu.unblock_member_confirm_description')}
        confirmLabel={t('community.action_menu.unblock_member_confirm_button')}
        onConfirm={() => {
          if (!member || blockSubmittingRef.current) return;
          blockSubmittingRef.current = true;
          blockMutation.mutate(
            { memberId: member.id, block: false },
            {
              onSuccess: () => {
                closeModal();
                revalidate();
              },
              onSettled: () => {
                blockSubmittingRef.current = false;
              },
            },
          );
        }}
        onCancel={closeModal}
        isLoading={blockMutation.isPending}
      />
    </CommunityMemberActionsContext.Provider>
  );
};
